import {
    Box,
    Button,
    ClickAwayListener,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";
import React, {
    useState
} from "react";

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Location } from "../../API";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import { useSnackbar } from "notistack";

type LocationContextMenuProps = {
    location: Location;
    viewLocation?: () => (void | Promise<void>);
    editLocation?: (locationId: string, locationName: string) => (void | Promise<void>);
    deleteLocation?: (locationId: string) => (void | Promise<void>);
};

export const LocationContextMenu = ({ location, editLocation, deleteLocation, viewLocation }: LocationContextMenuProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [isEditLocationDialogOpen, setIsEditLocationDialogOpen] = useState(false);
    const [isDeleteLocationDialogOpen, setIsDeleteLocationDialogOpen] = useState(false);
    const [stagedLocationName, setStagedLocationName] = useState(location.name);
    const snackbars = useSnackbar();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <ClickAwayListener onClickAway={handleClose}>
                <IconButton
                    onClick={handleClick}
                    size="small"
                >
                    <MoreVertIcon />
                </IconButton>
            </ClickAwayListener>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    horizontal: 'right',
                    vertical: 'center',
                }}
            >
                {viewLocation && (
                    <MenuItem
                        onClick={() => {
                            viewLocation();
                        }}
                    >
                        <ListItemIcon sx={{ color: "success.main" }}>
                            <ViewAgendaOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            View
                        </ListItemText>
                    </MenuItem>
                )}
                {editLocation && (
                    <MenuItem
                        onClick={() => {
                            setIsEditLocationDialogOpen(true);
                        }}
                    >
                        <ListItemIcon sx={{ color: "primary.main" }}>
                            <EditOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            Edit
                        </ListItemText>
                    </MenuItem>
                )}
                {deleteLocation && (
                    <MenuItem
                        onClick={() => {
                            setIsDeleteLocationDialogOpen(true);
                        }}
                    >
                        <ListItemIcon sx={{ color: "error.main" }}>
                            <DeleteOutlineIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            Delete
                        </ListItemText>
                    </MenuItem>
                )}
            </Menu>
            <Dialog
                open={isEditLocationDialogOpen}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Edit Location Name
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        value={stagedLocationName}
                        onChange={(e) => {
                            setStagedLocationName(e.target.value);
                        }}
                        size="small"
                    />
                </DialogContent>
                <DialogActions>
                    <Box display="flex" justifyContent="space-around" flex={1} gap={1}>
                        <Button
                            fullWidth
                            onClick={() => {
                                setIsEditLocationDialogOpen(false);
                                setStagedLocationName(location.name);
                            }}
                            variant="outlined"
                            size="small"
                            color="error"
                        >
                            Cancel
                        </Button>
                        <Button
                            fullWidth
                            onClick={async () => {
                                try {
                                    const trimmedLocationName = stagedLocationName?.trim();
                                    if (editLocation && trimmedLocationName && stagedLocationName !== location.name) {
                                        await editLocation(location.id, trimmedLocationName);
                                    }
                                    setIsEditLocationDialogOpen(false);
                                } catch (error) {
                                    snackbars.enqueueSnackbar("An error occurred while trying to edit the location name.", {
                                        variant: "error"
                                    });
                                }
                            }}
                            variant="contained"
                            size="small"
                        >
                            Save
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
            <Dialog
                open={isDeleteLocationDialogOpen}
                onClose={() => {
                    setIsDeleteLocationDialogOpen(false);
                }}
            >
                <DialogTitle>
                    Delete {location.name}
                </DialogTitle>
                <DialogContent>
                    Deleting this location will <strong>REMOVE ALL</strong> issues and assessments associated with it. Are you sure you want to delete this location?
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-around" }}>
                    <Button
                        onClick={() => {
                            setIsDeleteLocationDialogOpen(false);
                        }}
                        variant="outlined"
                        size="small"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {
                            try {
                                if (deleteLocation) {
                                    await deleteLocation(location.id);
                                }
                                setIsDeleteLocationDialogOpen(false);
                            } catch (error) {
                                snackbars.enqueueSnackbar("An error occurred while trying to delete the location.", {
                                    variant: "error"
                                });
                            }
                        }}
                        color="error"
                        variant="contained"
                        size="small"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};